import React from "react";

import { Svg, Props as SvgProps, svgNames as thelabuiSvgNames } from "../Svg";
import { ClickableProps } from "./types";

type ConditionalIcons = "download" | "external";

/**
 * Child projects might not want to use icons with these exact names, so map them
 */
export type IconMap<T extends string> = {
    [k in ConditionalIcons]: T;
};

/**
 * Uses a project's list of SVG names to create a component that can safely call
 * that project's Svg component, while prioritizing special use cases.
 */
export const createConditionalIcons = <T extends string>(
    svgNames: ReadonlyArray<T>,
    svgComponent: React.ComponentType<SvgProps<T>>,
) => {
    type Props = ClickableProps & {
        mappedConditionalIcons: IconMap<T>;
    };

    const isValidName = (name: string): name is T => {
        return svgNames.includes(name as T);
    };

    return ({ mappedConditionalIcons: icons, icon, ...props }: Props) => {
        const Component = svgComponent;
        let name: T | null = null;
        if ("download" in props) {
            name = icons.download;
        }
        if ("target" in props && props.target === "_blank") {
            name = icons.external;
        }
        if (icon && name === null) {
            if (typeof icon === "string") {
                if (isValidName(icon)) {
                    name = icon;
                } else {
                    /**
                     * The one thing that Clickable itself doesn't account for.
                     * Make it throw an obvious visual error.
                     */
                    return <>NO ICON NAMED {icon}</>;
                }
            } else {
                /**
                 * Curveball: This is a JSX element. Pressure valve in case you
                 * need to do something custom. (You probably don't.)
                 */
                return icon;
            }
        }
        return name ? (
            <Component
                name={name}
                visuallyHiddenText={null}
                aria-hidden={true}
            />
        ) : null;
    };
};

export const ConditionalIcons = createConditionalIcons(thelabuiSvgNames, Svg);
